import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Flame, Clock, ArrowRight, ShieldCheck } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { fetchCurrentAuction } from "@/lib/api";

const formatRemaining = (secs, isNl) => {
  if (!secs || secs <= 0) return isNl ? "Sluit binnenkort" : "Closing soon";
  const d = Math.floor(secs / 86400);
  const h = Math.floor((secs % 86400) / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (d > 0) return isNl ? `${d}d ${h}u` : `${d}d ${h}h`;
  if (h > 0) return isNl ? `${h}u ${m}m` : `${h}h ${m}m`;
  return isNl ? `${m}m` : `${m}m`;
};

/**
 * Live "Wie biedt nu?" strip. Renders only when an auction is currently open
 * AND there is at least 1 bid (otherwise it would feel empty). Drives traffic
 * from the homepage to the auction page.
 */
const LiveAuctionStrip = ({ showFallback = false }) => {
  const { lang } = useLang();
  const isNl = lang === "nl";
  const [a, setA] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchCurrentAuction();
        if (!cancelled && data && data.id && data.status === "open") setA(data);
      } catch (e) { /* no auction → render nothing */ }
    })();
    // Refresh every 60s for live countdown + bid count
    const t = setInterval(() => setTick((x) => x + 1), 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, [tick]);

  if (!a) {
    if (!showFallback) return null;
    return (
      <section className="hac-container py-6">
        <Link
          to="/auction"
          className="group flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-5 bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 border border-amber-200 rounded-2xl px-5 py-4 hover:border-amber-400 hover:shadow-sm transition-all"
          data-testid="live-auction-strip-fallback"
        >
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="shrink-0 w-10 h-10 rounded-full bg-amber-500 text-white flex items-center justify-center">
              <Flame size={18} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-amber-700 bg-white border border-amber-300 px-2 py-0.5 rounded-full">
                  {isNl ? "Wekelijkse veiling" : "Weekly auction"}
                </span>
                <span className="text-[11px] text-[#64748B]">{isNl ? "Volgende ronde opent binnenkort" : "Next round opens soon"}</span>
              </div>
              <div className="text-sm md:text-base text-[#0F172A] mt-1 font-medium leading-tight">
                {isNl
                  ? <>Bieden vanaf <strong className="text-amber-700">€25</strong> · 1 winnende app per week wordt uitgelicht · lijstprijs <span className="line-through text-[#94A3B8]">€499</span></>
                  : <>Bid from <strong className="text-amber-700">€25</strong> · 1 winning app featured per week · list price <span className="line-through text-[#94A3B8]">€499</span></>}
              </div>
              <p className="text-[11px] text-emerald-700 mt-1 inline-flex items-center gap-1"><ShieldCheck size={10} /> {isNl ? "100% privé bieden" : "100% private bidding"}</p>
            </div>
          </div>
          <span className="shrink-0 inline-flex items-center gap-1.5 bg-amber-500 group-hover:bg-amber-600 text-white text-xs font-bold px-4 py-2 rounded-full transition-colors">
            {isNl ? "Bekijk veiling" : "View auction"} <ArrowRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
          </span>
        </Link>
      </section>
    );
  }
  const bidCount = a.bid_count || 0;
  const highest = a.current_highest_bid_eur;
  const minNext = a.min_next_bid_eur || a.starting_price_eur || 49;

  return (
    <section className="hac-container py-6">
      <Link
        to="/auction"
        className="group flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-5 bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 border border-amber-200 rounded-2xl px-5 py-4 hover:border-amber-400 hover:shadow-sm transition-all"
        data-testid="live-auction-strip"
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="shrink-0 w-10 h-10 rounded-full bg-amber-500 text-white flex items-center justify-center animate-pulse">
            <Flame size={18} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-amber-700 bg-white border border-amber-300 px-2 py-0.5 rounded-full">
                {isNl ? "Live veiling" : "Live auction"}
              </span>
              {a.seconds_remaining > 0 && (
                <span className="inline-flex items-center gap-1 text-[11px] text-[#64748B]"><Clock size={11} /> {formatRemaining(a.seconds_remaining, isNl)} {isNl ? "te gaan" : "left"}</span>
              )}
            </div>
            <div className="text-sm md:text-base text-[#0F172A] mt-1 font-medium leading-tight">
              {bidCount > 0 ? (
                isNl
                  ? <>🔥 <strong>{bidCount}</strong> {bidCount === 1 ? "bedrijf biedt" : "bedrijven bieden"} mee deze week · {highest ? <>hoogste bod <strong className="text-amber-700">€{Math.round(highest)}</strong></> : <>vanaf <strong>€{Math.round(minNext)}</strong></>}</>
                  : <>🔥 <strong>{bidCount}</strong> {bidCount === 1 ? "company is bidding" : "companies are bidding"} this week · {highest ? <>top bid <strong className="text-amber-700">€{Math.round(highest)}</strong></> : <>from <strong>€{Math.round(minNext)}</strong></>}</>
              ) : (
                isNl
                  ? <>✨ Open spot deze week · bieden vanaf <strong className="text-amber-700">€{Math.round(minNext)}</strong> · 1 winnende app per week</>
                  : <>✨ Open slot this week · bid from <strong className="text-amber-700">€{Math.round(minNext)}</strong> · 1 winning app per week</>
              )}
            </div>
            <p className="text-[11px] text-emerald-700 mt-1 inline-flex items-center gap-1"><ShieldCheck size={10} /> {isNl ? "100% privé bieden" : "100% private bidding"}</p>
          </div>
        </div>
        <span className="shrink-0 inline-flex items-center gap-1.5 bg-amber-500 group-hover:bg-amber-600 text-white text-xs font-bold px-4 py-2 rounded-full transition-colors">
          {isNl ? "Bekijk veiling" : "View auction"} <ArrowRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
        </span>
      </Link>
    </section>
  );
};

export default LiveAuctionStrip;
